import { useState } from 'react'

import { useSaveSettings, useSettings } from '../../hooks/queries'

/**
 * Ort und Wetter: ist es eingerichtet?
 *
 * Beim Einrichten fehlte genau diese Auskunft. Der Server weiß es und sagt es
 * auch (`/api/settings` → `services`), aber zu sehen war es nirgends — der
 * Weg war, die Adresse von Hand im Browser aufzurufen und im JSON
 * nachzusehen. Genau die Art Umweg, die diese Anwendung sonst vermeidet:
 * ausgeliefert wird per FTP, die `.env` bearbeitet ein Mensch mit einem
 * Texteditor, und ob ein Tippfehler darin steht, soll die Anwendung sagen.
 *
 * **Die Karte zeigt sich nur der Verwaltung** — und zwar daran erkennbar,
 * dass der Server die Decke mitschickt, nicht weil die Oberfläche aus der
 * Rolle schließt. Dasselbe Muster wie in `LimitsCard`.
 *
 * **„Nicht eingerichtet" ist keine Fehlermeldung.** Ohne die Dienste läuft
 * alles weiter; ein Vorschlag aus der Route heißt dann eben „Aufenthalt,
 * 2 Std." statt „Trondheim". Das steht auch so da — sonst sucht jemand einen
 * Fehler, den es nicht gibt.
 */
export function DiensteCard() {
  const settings = useSettings()
  const save = useSaveSettings()
  const [entwurf, setEntwurf] = useState<string | null>(null)
  const [gemeldet, setGemeldet] = useState(false)
  const daten = settings.data

  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling) return null

  const dienste = daten.services ?? { place: false, weather: false }
  const grenzen = daten.limits
  const umkreis = entwurf ?? String(daten.settings.poi_radius_m)
  const geaendert =
    Number.isInteger(Number(umkreis)) && Number(umkreis) !== daten.settings.poi_radius_m
  const beides = dienste.place && dienste.weather
  const nichts = !dienste.place && !dienste.weather

  /**
   * **Der Fall, den man sonst nirgends sieht.** Alle Angaben stehen in der
   * `.env`, und trotzdem läuft nichts — weil unter dem eingetragenen Pfad
   * keine lesbare Datei liegt. Ein Tippfehler, ein falsches Verzeichnis,
   * vergessene Leserechte; getippt wird der Pfad von Hand, per FTP, unterwegs.
   *
   * Vorher sagte die Statuszeile in genau diesem Fall „eingerichtet", die
   * Knöpfe erschienen, und jeder Abruf ging still ins Leere.
   */
  const detail = daten.services_detail
  const schluesselFehlt =
    detail !== undefined &&
    !detail.key_readable &&
    (detail.place_configured || detail.weather_configured)

  return (
    <div className="card" style={{ margin: '0 auto 16px', boxShadow: 'none' }}>
      <h1 style={{ fontSize: 17 }}>Ort und Wetter</h1>
      <p className="card__lead" style={{ marginBottom: 10 }}>
        Was ein Punkt auf der Karte über sich sagen kann — der Ortsname und das
        Wetter zu der Stunde, in der du dort warst.
      </p>

      <p className="dienst">
        <span className="dienst__name">Ortsnamen</span>
        <Stand an={dienste.place} />
      </p>
      <p className="dienst">
        <span className="dienst__name">Wetter</span>
        <Stand an={dienste.weather} />
      </p>

      {schluesselFehlt ? (
        <p className="dienst__fehler" role="alert">
          <strong>Die Schlüsseldatei fehlt.</strong> Die Angaben stehen in der{' '}
          <code>.env</code>, aber unter <code>APPLE_PRIVATE_KEY_PATH</code>
          {' '}liegt keine Datei, die der Server lesen kann. Prüfe den Pfad, das
          Verzeichnis und die Leserechte — ein relativer Pfad zählt vom
          Verzeichnis der <code>index.php</code>. Bis dahin bleiben Ortsnamen und
          Wetter aus; sonst läuft alles weiter.
        </p>
      ) : nichts ? (
        <p className="card__lead" style={{ marginTop: 10, marginBottom: 0 }}>
          Das ist in Ordnung: ohne diese Dienste läuft alles weiter. Ein
          Vorschlag aus der Route heißt dann „Aufenthalt, 2 Std." statt nach
          seinem Ort. Wer sie einrichten möchte, findet die Schritte in der
          Installationsanleitung.
        </p>
      ) : !beides ? (
        <p className="card__lead" style={{ marginTop: 10, marginBottom: 0 }}>
          Eines von beiden fehlt. Nachzusehen ist in der <code>.env</code> auf
          dem Server:
        </p>
      ) : (
        <p className="card__lead" style={{ marginTop: 10, marginBottom: 0 }}>
          Beides ist da. Die Angaben werden geholt, sobald du eine Karte
          öffnest — einmal je Highlight, nie beim Anzeigen. „In der Nähe
          finden" im Highlightformular nutzt denselben Zugang, fragt aber auf
          Knopfdruck.
        </p>
      )}

      {!beides && !nichts && !schluesselFehlt && (
        <ul className="dienst__zeilen">
          <li>APPLE_TEAM_ID</li>
          <li>APPLE_MAPS_KEY_ID</li>
          <li>APPLE_WEATHER_KEY_ID</li>
          <li>APPLE_WEATHER_SERVICE_ID</li>
          <li>APPLE_PRIVATE_KEY_PATH</li>
        </ul>
      )}

      {/* **Nur wo die Ortssuche läuft.** Ohne sie gibt es den Knopf „In der
          Nähe finden" nicht, und eine Zahl für etwas Abgeschaltetes
          einzustellen wäre ein Rätsel statt einer Einstellung. */}
      {dienste.place && grenzen !== undefined && (
        <label className="field" style={{ marginTop: 14, marginBottom: 4 }}>
          <span className="field__label">Umkreis der Umgebungssuche (Meter)</span>
          <input
            className="field__input"
            type="number"
            inputMode="numeric"
            name="poi_radius_m"
            value={umkreis}
            min={grenzen.poi_radius_m.min}
            max={grenzen.poi_radius_m.max}
            onChange={(e) => {
              setGemeldet(false)
              setEntwurf(e.target.value)
            }}
          />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Wie weit „In der Nähe finden" im Highlightformular sucht. 500 m sind
            Gehweite: man war dort, das Lokal ist um die Ecke. Mehr bringt mehr
            Treffer, aber auch solche, an denen niemand war.
          </span>
          <span style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
            <button
              className="button button--outline button--small"
              type="button"
              disabled={!geaendert || save.isPending}
              onClick={() =>
                save.mutate(
                  { poi_radius_m: Number(umkreis) },
                  {
                    onSuccess: () => {
                      setEntwurf(null)
                      setGemeldet(true)
                    },
                  },
                )
              }
            >
              {save.isPending ? 'Einen Moment …' : 'Speichern'}
            </button>
            {gemeldet && !save.isPending && (
              <span style={{ fontSize: 12, color: 'var(--accent)' }}>Gespeichert.</span>
            )}
            {save.isError && (
              <span style={{ fontSize: 12 }} role="alert">
                {save.error instanceof Error ? save.error.message : 'Das hat nicht geklappt.'}
              </span>
            )}
          </span>
        </label>
      )}

      {!beides && (
        // Kein Neustart nötig -- hier läuft kein Prozess durch, der Server
        // liest die Datei bei jeder Anfrage neu. Die Oberfläche hält den Wert
        // allerdings zehn Minuten im Zwischenspeicher.
        <p className="dienst__hinweis">
          Der Server liest die <code>.env</code> bei jeder Anfrage neu — ein
          Neustart ist nicht nötig. Diese Seite zeigt den Stand nach dem
          nächsten Neuladen.
        </p>
      )}
    </div>
  )
}

function Stand({ an }: { an: boolean }) {
  return (
    <span className={an ? 'dienst__stand dienst__stand--an' : 'dienst__stand'}>
      {an ? 'eingerichtet' : 'nicht eingerichtet'}
    </span>
  )
}
